/*
 * <dwm-quote-slider> — one review at a time (sections/dwm-quotes.liquid, layout "Slider").
 *
 * One state: the index. select() is the only place it changes; it shows that review, marks its name (aria-current),
 * updates the counter and announces the change politely. Previous / next wrap around; the names jump directly;
 * ArrowLeft / ArrowRight work while focus is on the controls. No timers, no autoplay.
 * Without this script the controls stay hidden and every review is listed.
 * In the Theme Editor, selecting a review block shows that review.
 */
class DwmQuoteSlider extends HTMLElement {
  #slides = [];
  #names = [];
  #controls = null;
  #current = null;
  #live = null;
  #index = 0;

  connectedCallback() {
    this.#slides = [...this.querySelectorAll('[data-qs-slide]')];
    this.#names = [...this.querySelectorAll('[data-qs-go]')];
    this.#controls = this.querySelector('[data-qs-controls]');
    this.#current = this.querySelector('[data-qs-current]');
    this.#live = this.querySelector('[data-qs-live]');

    if (!this.#controls || this.#slides.length < 2) {
      return;
    }

    this.#controls.removeAttribute('hidden');
    this.classList.add('is-enhanced');
    this.#controls.addEventListener('click', this.#onClick);
    this.#controls.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
    this.select(0, { announce: false });
  }

  disconnectedCallback() {
    this.#controls?.removeEventListener('click', this.#onClick);
    this.#controls?.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
  }

  select(index, { announce = true } = {}) {
    const count = this.#slides.length;
    this.#index = ((index % count) + count) % count;

    this.#slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('inert', !active);
    });

    this.#names.forEach((name, i) => {
      if (i === this.#index) {
        name.setAttribute('aria-current', 'true');
      } else {
        name.removeAttribute('aria-current');
      }
    });

    if (this.#current) {
      this.#current.textContent = String(this.#index + 1).padStart(2, '0');
    }

    if (announce && this.#live) {
      const company = this.#names[this.#index]?.textContent.replace(/^\s*\d+\s*/, '').trim() ?? '';
      this.#live.textContent = `Review ${this.#index + 1} van ${count}${company ? `: ${company}` : ''}`;
    }
  }

  #onClick = (event) => {
    const go = event.target.closest('[data-qs-go]');

    if (go) {
      this.select(Number(go.dataset.qsGo));
    } else if (event.target.closest('[data-qs-prev]')) {
      this.select(this.#index - 1);
    } else if (event.target.closest('[data-qs-next]')) {
      this.select(this.#index + 1);
    }
  };

  #onKeydown = (event) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.select(this.#index + (event.key === 'ArrowRight' ? 1 : -1));
    }
  };

  #onBlockSelect = (event) => {
    const index = this.#slides.findIndex((slide) => slide.contains(event.target) || slide === event.target);

    if (index >= 0) {
      this.select(index, { announce: false });
    }
  };
}

if (!customElements.get('dwm-quote-slider')) {
  customElements.define('dwm-quote-slider', DwmQuoteSlider);
}
